import React, {useEffect, useState} from 'react'
import {useRouter} from 'next/router'
import Link from 'next/link'
import {useTranslation} from 'next-i18next'

import {CategoryGroup, TopBar, TransparentButton} from '@miro-site/templates-design'
import {Box, useDebounce} from '@miro-site/core'
import {useServicesContext} from '@miro-site/services'
import {Datalayer} from '@miro-site/analytics'
import {useLocalization} from '@miro-site/localization'

import toSearchResult from '../data/mappers/toSearchResult'
import formatSlug from '../utils/formatSlug'
import {TemplateSearchData} from '../utils/searchTemplates'

export interface TemplatesSearchProps {
  stickyFixingPoint?: React.MutableRefObject<HTMLElement>
  categoryGroups: CategoryGroup[]
  withBackButton?: boolean
  isEditableTemplatesExperiment?: boolean
}

const TemplatesSearch: React.FC<TemplatesSearchProps> = props => {
  const router = useRouter()

  const {
    stickyFixingPoint,
    categoryGroups = [],
    withBackButton = false,
    isEditableTemplatesExperiment,
  } = props

  const [isSticky, setSticky] = useState(false)

  const [searchResult, setSearchResult] = useState<TemplateSearchData[]>([])

  const [isSearching, setIsSearching] = useState<boolean>(false)

  const [searchValue, setSearchValue] = useState('')

  const debouncedSearchValue = useDebounce(searchValue, 1000)
  const debouncedSearchValueFast = useDebounce(searchValue, 200)
  const {analytics} = useServicesContext()

  const {currentLocale} = useLocalization()
  const {t} = useTranslation('common')

  const trackSearch = (searchText: string, countResult: number) => {
    analytics.sendEvent(
      new Datalayer('templatesSearch', {
        searchText,
        countResult,
      }),
    )
  }

  useEffect(() => {
    const isIntersectionObserverSupported =
      'IntersectionObserver' in window && 'IntersectionObserverEntry' in window

    if (isIntersectionObserverSupported && stickyFixingPoint?.current) {
      const element = stickyFixingPoint.current

      const observer = new IntersectionObserver(
        ([entry]) => {
          setSticky(!entry.isIntersecting)
        },
        {rootMargin: '200px 0px 0px 0px'},
      )

      observer.observe(element)

      return () => {
        observer.unobserve(element)
      }
    }
  }, [stickyFixingPoint, isEditableTemplatesExperiment])

  useEffect(() => {
    async function fetchTemplates() {
      let result = []

      setIsSearching(true)

      if (searchValue) {
        const searchTemplates = (await import('../utils/searchTemplates')).default
        result = await searchTemplates(searchValue, currentLocale)
      }

      setSearchResult(result)
      setIsSearching(false)
    }

    fetchTemplates()
  }, [currentLocale, debouncedSearchValueFast, searchValue])

  useEffect(() => {
    if (searchValue) {
      trackSearch(searchValue, searchResult.length)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, searchResult.length, searchValue])

  useEffect(() => {
    searchResult.map(toSearchResult).forEach(r => {
      router.prefetch(r.slug)
    })
  }, [router, searchResult])

  return (
    <Box $styles={{height: 54}}>
      <TopBar
        statusMessages={{
          NO_RESULT: t('search-not-found'),
          TYPE_MORE: t('search-type-more'),
          SEARCHING: t('search-searching'),
        }}
        searchResult={searchResult.map(toSearchResult)}
        isSearching={isSearching}
        onSearch={val => setSearchValue(val)}
        onCategoryNavOpened={() => {
          categoryGroups.forEach(g => {
            g.categories.forEach(c => router.prefetch(c.slug))
          })
        }}
        onSelectTemplate={({slug}) => {
          router.push(slug)
        }}
        onSelectCategory={category => {
          router.push(category.slug)
        }}
        backButton={
          withBackButton && (
            <Link href={formatSlug('', currentLocale)} passHref legacyBehavior>
              <TransparentButton $size="small">← {t('all-templates-button')}</TransparentButton>
            </Link>
          )
        }
        categoriesButtonLabel={t('categories-button')}
        rootGroups={categoryGroups}
        isSticky={isSticky}
      />
    </Box>
  )
}

export default TemplatesSearch
